import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  User,
  Shield,
  CreditCard,
  LogOut,
  Check,
  Sparkles,
  Share2,
  Bell,
  Tags,
  Lock,
  Download,
  Trash2,
  QrCode,
  Smartphone,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import { isPremiumPlan, planLabel } from '../lib/plans';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useCategories } from '../hooks/useCategories';
import { CATEGORY_META, type CategoryBudget } from '../types';

const profileSchema = z.object({
  full_name: z.string().min(2, 'Name too short').max(64),
});

const passwordSchema = z
  .object({
    password: z.string().min(8, 'Min 8 characters').regex(/[A-Z]/).regex(/[0-9]/),
    confirmPassword: z.string(),
  })
  .refine(d => d.password === d.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

type SettingsTab =
  | 'profilo'
  | 'billing'
  | 'notifiche'
  | 'categorie'
  | 'sicurezza'
  | 'privacy';

interface NotificationPrefs {
  emailOverBudget: boolean;
  emailRecurring: boolean;
  emailTips: boolean;
  emailWeeklyReport: boolean;
  pushOverBudget: boolean;
  pushRecurring: boolean;
  pushTips: boolean;
  recurringReminderOffsets: number[];
}

const defaultNotificationPrefs: NotificationPrefs = {
  emailOverBudget: true,
  emailRecurring: true,
  emailTips: true,
  emailWeeklyReport: true,
  pushOverBudget: false,
  pushRecurring: false,
  pushTips: false,
  recurringReminderOffsets: [7, 5, 3, 1],
};

const tabs: Array<{ key: SettingsTab; label: string; icon: React.ReactNode }> = [
  { key: 'profilo', label: 'Profilo', icon: <User size={15} /> },
  { key: 'billing', label: 'Piano & Billing', icon: <CreditCard size={15} /> },
  { key: 'notifiche', label: 'Notifiche', icon: <Bell size={15} /> },
  { key: 'categorie', label: 'Categorie', icon: <Tags size={15} /> },
  { key: 'sicurezza', label: 'Sicurezza', icon: <Shield size={15} /> },
  { key: 'privacy', label: 'Privacy/GDPR', icon: <Lock size={15} /> },
];

const premiumFeatures = [
  'Unlimited expenses',
  'AI auto-categorization',
  'Advanced analytics',
  'Recurring expense tracking',
  'Priority support',
];

function getCategoryLabel(category: string): string {
  const meta = CATEGORY_META[category as keyof typeof CATEGORY_META];
  if (meta) return meta.label;
  return category
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export default function Settings() {
  const { profile, user, signOut, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { categories, refetch: refetchCategories } = useCategories();

  const [activeTab, setActiveTab] = useState<SettingsTab>('profilo');
  const [upgradingPlan, setUpgradingPlan] = useState(false);
  const [inviteCodeInput, setInviteCodeInput] = useState('');
  const [joiningInvite, setJoiningInvite] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPrefs>(defaultNotificationPrefs);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [categoryBudgets, setCategoryBudgets] = useState<Record<string, string>>({});
  const [savingBudgets, setSavingBudgets] = useState(false);

  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryEmoji, setNewCategoryEmoji] = useState('🏷️');
  const [savingCategory, setSavingCategory] = useState(false);

  const [aalLabel, setAalLabel] = useState('N/A');
  const [currentSessionLabel, setCurrentSessionLabel] = useState('N/A');
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  const [mfaFactors, setMfaFactors] = useState<Array<{ id: string; status?: string }>>([]);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [enrollingMfa, setEnrollingMfa] = useState(false);
  const [verifyingMfa, setVerifyingMfa] = useState(false);
  const [totpQrCode, setTotpQrCode] = useState('');
  const [totpUri, setTotpUri] = useState('');
  const [pendingFactorId, setPendingFactorId] = useState('');
  const [pendingChallengeId, setPendingChallengeId] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaFriendlyName, setMfaFriendlyName] = useState('BudgetAI Authenticator');

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const [downloadingData, setDownloadingData] = useState(false);

  const isPremium = isPremiumPlan(profile?.plan);
  const maxCustomCategories = 3;
  const budgetCategories = useMemo(() => {
    const builtins = Object.keys(CATEGORY_META);
    const custom = categories.map((c) => c.name.toLowerCase());
    return Array.from(new Set([...builtins, ...custom]));
  }, [categories]);

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: { full_name: profile?.full_name ?? '' },
  });

  const pwForm = useForm<PasswordForm>({ resolver: zodResolver(passwordSchema) });

  const hasEnrolledMfa = useMemo(
    () => mfaFactors.some(f => (f.status ?? '').toLowerCase() === 'verified' || !f.status),
    [mfaFactors],
  );

  useEffect(() => {
    profileForm.reset({ full_name: profile?.full_name ?? '' });
  }, [profile?.full_name, profileForm]);

  useEffect(() => {
    const raw = (profile as unknown as { notification_prefs?: NotificationPrefs | null })?.notification_prefs;
    setNotificationPrefs({ ...defaultNotificationPrefs, ...(raw ?? {}) });
  }, [profile]);

  const refreshSessionInfo = async () => {
    setLoadingSessions(true);
    try {
      const [sessionRes, aalRes] = await Promise.all([
        supabase.auth.getSession(),
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      ]);

      const expiresAt = sessionRes.data.session?.expires_at;
      const expiresLabel = expiresAt
        ? format(new Date(expiresAt * 1000), 'dd MMM yyyy HH:mm')
        : 'Sessione locale';
      setCurrentSessionLabel(expiresLabel);
      const aalData = aalRes.data;
      setAalLabel(`${aalData?.currentLevel ?? 'aal1'} → ${aalData?.nextLevel ?? 'aal2'}`);
    } catch {
      setCurrentSessionLabel('Non disponibile');
      setAalLabel('Non disponibile');
    } finally {
      setLoadingSessions(false);
    }
  };

  const refreshMfaFactors = async () => {
    setMfaLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;

      const factors = ((data?.totp ?? []) as Array<{ id: string; status?: string }>).map(f => ({
        id: f.id,
        status: f.status,
      }));
      setMfaFactors(factors);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore nel caricamento MFA');
    } finally {
      setMfaLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'sicurezza') {
      void refreshMfaFactors();
      void refreshSessionInfo();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!user || activeTab !== 'notifiche') return;

    const loadBudgets = async () => {
      const { data, error } = await supabase
        .from('category_budgets')
        .select('category, monthly_limit')
        .eq('user_id', user.id);

      if (error) {
        toast.error(error.message);
        return;
      }

      const mapped: Record<string, string> = {};
      for (const row of (data ?? []) as Array<Pick<CategoryBudget, 'category' | 'monthly_limit'>>) {
        mapped[row.category.toLowerCase()] = String(row.monthly_limit);
      }
      setCategoryBudgets(mapped);
    };

    void loadBudgets();
  }, [activeTab, user]);

  const onSaveProfile = async ({ full_name }: ProfileForm) => {
    try {
      const { error } = await supabase
        .from('users')
        .update({ full_name })
        .eq('id', user!.id);
      if (error) throw error;
      await refreshProfile();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed');
    }
  };

  const onChangePassword = async ({ password }: PasswordForm) => {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success('Password updated');
      pwForm.reset();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Password update failed');
    }
  };

  const handleUpgrade = async () => {
    setUpgradingPlan(true);
    try {
      navigate('/pricing');
    } finally {
      setUpgradingPlan(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('upgrade-plan', {
        body: { action: 'create-portal' }
      });
      if (error) {
        let detailedMessage: string | null = null;
        const maybeContext = (error as { context?: { json?: () => Promise<unknown> } }).context;
        if (maybeContext?.json) {
          try {
            const payload = await maybeContext.json() as { error?: string; message?: string };
            detailedMessage = payload?.error ?? payload?.message ?? null;
          } catch {
            detailedMessage = null;
          }
        }
        throw new Error(detailedMessage ?? error.message);
      }
      const url = (data as { url?: string })?.url;
      if (!url) throw new Error('URL customer portal non disponibile');
      window.location.href = url;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Impossibile aprire la gestione abbonamento');
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleSignOutAll = async () => {
    setLoggingOutAll(true);
    try {
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;
      toast.success('Tutte le sessioni sono state chiuse');
      navigate('/');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Impossibile chiudere tutte le sessioni');
    } finally {
      setLoggingOutAll(false);
    }
  };

  const inviteCode = profile?.invite_code?.toUpperCase() || '';
  const inviteLink = inviteCode
    ? `${window.location.origin}/invite/${encodeURIComponent(inviteCode)}`
    : '';

  const copyInviteLink = async () => {
    if (!inviteLink) {
      toast.error('Invite link non disponibile');
      return;
    }
    await navigator.clipboard.writeText(inviteLink);
    toast.success('Invite link copiato');
  };

  const joinWithInvite = async () => {
    if (!inviteCodeInput.trim()) {
      toast.error('Inserisci un codice invito');
      return;
    }

    setJoiningInvite(true);
    try {
      const { error } = await supabase.rpc('apply_invite_code', {
        p_code: inviteCodeInput.trim().toUpperCase(),
      });
      if (error) {
        let detailedMessage: string | null = null;
        const maybeContext = (error as { context?: { json?: () => Promise<unknown> } }).context;
        if (maybeContext?.json) {
          try {
            const payload = await maybeContext.json() as { error?: string; message?: string };
            detailedMessage = payload?.error ?? payload?.message ?? null;
          } catch {
            detailedMessage = null;
          }
        }
        throw new Error(detailedMessage ?? error.message);
      }
      toast.success('Invito applicato correttamente');
      setInviteCodeInput('');
      await refreshProfile();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Impossibile applicare il codice');
    } finally {
      setJoiningInvite(false);
    }
  };

  const saveNotifications = async () => {
    if (!user) return;
    setSavingNotifications(true);
    try {
      const safePrefs: NotificationPrefs = {
        emailOverBudget: Boolean(notificationPrefs.emailOverBudget),
        emailRecurring: Boolean(notificationPrefs.emailRecurring),
        emailTips: Boolean(notificationPrefs.emailTips),
        emailWeeklyReport: Boolean(notificationPrefs.emailWeeklyReport),
        pushOverBudget: Boolean(notificationPrefs.pushOverBudget),
        pushRecurring: Boolean(notificationPrefs.pushRecurring),
        pushTips: Boolean(notificationPrefs.pushTips),
        recurringReminderOffsets: (notificationPrefs.recurringReminderOffsets ?? [])
          .map((v) => Number(v))
          .filter((v) => Number.isFinite(v) && v > 0)
          .slice(0, 8),
      };

      const { error } = await supabase
        .from('users')
        .update({ notification_prefs: safePrefs })
        .eq('id', user.id);
      if (error) throw error;
      toast.success('Preferenze notifiche salvate');
      await refreshProfile();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore salvataggio notifiche';
      if (msg.toLowerCase().includes('notification_prefs')) {
        toast.error('Errore schema DB notification_prefs: applica le migrazioni 011/014 e riprova');
      } else {
        toast.error(msg);
      }
    } finally {
      setSavingNotifications(false);
    }
  };

  const togglePushPref = async (prefKey: 'pushOverBudget' | 'pushRecurring' | 'pushTips', checked: boolean) => {
    if (checked) {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        toast.error('Push non supportate da questo browser');
        return;
      }

      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          toast.error('Permesso notifiche push negato');
          return;
        }
      }

      if (Notification.permission !== 'granted') {
        toast.error('Abilita le notifiche nelle impostazioni del browser');
        return;
      }
    }

    setNotificationPrefs(prev => ({
      ...prev,
      [prefKey]: checked,
    }));
  };

  const saveCategoryBudgets = async () => {
    if (!user) return;
    if (!isPremium) {
      toast.error('Feature disponibile solo su Premium');
      return;
    }

    setSavingBudgets(true);
    try {
      const rows = Object.entries(categoryBudgets)
        .map(([category, amount]) => ({ category, monthly_limit: Number(amount) }))
        .filter((row) => Number.isFinite(row.monthly_limit) && row.monthly_limit > 0);

      const categoriesToKeep = new Set(rows.map((r) => r.category));

      if (rows.length > 0) {
        const { error: upsertError } = await supabase
          .from('category_budgets')
          .upsert(
            rows.map((r) => ({
              user_id: user.id,
              category: r.category,
              monthly_limit: r.monthly_limit,
            })),
            { onConflict: 'user_id,category' },
          );
        if (upsertError) throw upsertError;
      }

      const { data: currentRows, error: readError } = await supabase
        .from('category_budgets')
        .select('category')
        .eq('user_id', user.id);
      if (readError) throw readError;

      const stale = (currentRows ?? [])
        .map((r) => String((r as { category: string }).category).toLowerCase())
        .filter((category) => !categoriesToKeep.has(category));

      if (stale.length > 0) {
        const { error: deleteError } = await supabase
          .from('category_budgets')
          .delete()
          .eq('user_id', user.id)
          .in('category', stale);
        if (deleteError) throw deleteError;
      }

      toast.success('Budget mensili salvati');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore salvataggio budget');
    } finally {
      setSavingBudgets(false);
    }
  };

  const addCustomCategory = async () => {
    if (!user) return;
    if (!newCategoryName.trim()) {
      toast.error('Inserisci un nome categoria');
      return;
    }

    if (!isPremium && categories.length >= maxCustomCategories) {
      toast.error(`Piano Free: massimo ${maxCustomCategories} categorie custom`);
      return;
    }

    const normalizedName = newCategoryName
      .trim()
      .split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');

    setSavingCategory(true);
    try {
      const { error } = await supabase
        .from('categories')
        .insert({
          user_id: user.id,
          name: normalizedName,
          emoji: newCategoryEmoji.trim() || '🏷️',
          count: 1,
        });
      if (error) throw error;
      toast.success('Categoria aggiunta');
      setNewCategoryName('');
      setNewCategoryEmoji('🏷️');
      await refetchCategories();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore creazione categoria');
    } finally {
      setSavingCategory(false);
    }
  };

  const deleteCustomCategory = async (categoryId: string) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId)
        .eq('user_id', user.id);
      if (error) throw error;
      toast.success('Categoria eliminata');
      await refetchCategories();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore eliminazione categoria');
    }
  };

  const enrollTotp = async () => {
    setEnrollingMfa(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: mfaFriendlyName,
      });
      if (error) throw error;

      const qrRaw = data.totp.qr_code;
      const asDataUri = qrRaw.startsWith('<svg')
        ? `data:image/svg+xml;utf8,${encodeURIComponent(qrRaw)}`
        : qrRaw;
      setTotpQrCode(asDataUri);
      setTotpUri(data.totp.uri);
      setPendingFactorId(data.id);

      const challenge = await supabase.auth.mfa.challenge({ factorId: data.id });
      if (challenge.error) throw challenge.error;
      setPendingChallengeId(challenge.data.id);

      toast.success('Scannerizza il QR e inserisci il codice a 6 cifre');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore attivazione MFA');
    } finally {
      setEnrollingMfa(false);
    }
  };

  const generateBackupCodes = (): string[] => {
    const codes: string[] = [];
    for (let i = 0; i < 10; i += 1) {
      const partA = Math.random().toString(36).slice(2, 6).toUpperCase();
      const partB = Math.random().toString(36).slice(2, 6).toUpperCase();
      codes.push(`${partA}-${partB}`);
    }
    return codes;
  };

  const hashText = async (value: string): Promise<string> => {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(value);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const downloadBackupCodes = async () => {
    if (!user) return;
    try {
      const plainCodes = generateBackupCodes();
      const hashedCodes = await Promise.all(plainCodes.map(hashText));

      const { error } = await supabase
        .from('users')
        .update({ mfa_backup_codes_hashes: hashedCodes })
        .eq('id', user.id);
      if (error) throw error;

      const payload = [
        'BudgetAI - Codici di backup MFA',
        `Utente: ${profile?.email ?? 'n/a'}`,
        `Generati: ${new Date().toLocaleString('it-IT')}`,
        '',
        ...plainCodes,
        '',
        'Conserva questi codici offline. Ogni codice e monouso.',
      ].join('\n');

      const blob = new Blob([payload], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'budgetai-backup-codes.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Backup codes generati e scaricati');
      await refreshProfile();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Impossibile generare backup codes');
    }
  };

  const verifyTotp = async () => {
    if (!pendingFactorId || !pendingChallengeId || !totpCode.trim()) {
      toast.error('Completa i passaggi MFA prima di verificare');
      return;
    }

    setVerifyingMfa(true);
    try {
      const { error } = await supabase.auth.mfa.verify({
        factorId: pendingFactorId,
        challengeId: pendingChallengeId,
        code: totpCode.trim(),
      });
      if (error) throw error;

      toast.success('MFA TOTP attivata con successo');
      setTotpCode('');
      setTotpQrCode('');
      setTotpUri('');
      setPendingFactorId('');
      setPendingChallengeId('');
      await refreshMfaFactors();
      await downloadBackupCodes();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Codice MFA non valido');
    } finally {
      setVerifyingMfa(false);
    }
  };

  const disableMfa = async () => {
    const factorId = mfaFactors[0]?.id;
    if (!factorId) {
      toast.error('Nessun fattore MFA attivo');
      return;
    }
    setMfaLoading(true);
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
      toast.success('MFA disattivata');
      await refreshMfaFactors();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore disattivazione MFA');
    } finally {
      setMfaLoading(false);
    }
  };

  const downloadUserData = async () => {
    if (!user) return;
    setDownloadingData(true);
    try {
      const [
        userRow,
        expensesRow,
        incomesRow,
        categoriesRow,
        referralsAsInviter,
        referralsAsInvited,
      ] = await Promise.all([
        supabase.from('users').select('*').eq('id', user.id).single(),
        supabase.from('expenses').select('*').eq('user_id', user.id).order('date', { ascending: false }),
        supabase.from('incomes').select('*').eq('user_id', user.id).order('date', { ascending: false }),
        supabase.from('categories').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('user_referrals').select('*').eq('inviter_id', user.id),
        supabase.from('user_referrals').select('*').eq('invited_id', user.id),
      ]);

      if (userRow.error) throw userRow.error;
      if (expensesRow.error) throw expensesRow.error;
      if (incomesRow.error) throw incomesRow.error;
      if (categoriesRow.error) throw categoriesRow.error;

      const referralsExport = {
        asInviter: referralsAsInviter.error ? [] : (referralsAsInviter.data ?? []),
        asInvited: referralsAsInvited.error ? [] : (referralsAsInvited.data ?? []),
      };

      const exportPayload = {
        exportedAt: new Date().toISOString(),
        user: userRow.data,
        expenses: expensesRow.data ?? [],
        incomes: incomesRow.data ?? [],
        categories: categoriesRow.data ?? [],
        referrals: referralsExport,
      };

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `budgetai-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Esportazione GDPR completata');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore esportazione dati');
    } finally {
      setDownloadingData(false);
    }
  };

  const deleteAccountCascade = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== 'ELIMINA') {
      toast.error('Scrivi ELIMINA per confermare');
      return;
    }

    setDeletingAccount(true);
    try {
      const { error } = await supabase.rpc('delete_my_account');
      if (error) throw error;
      toast.success('Account eliminato definitivamente');
      setOpenDeleteModal(false);
      navigate('/');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore eliminazione account');
    } finally {
      setDeletingAccount(false);
    }
  };

  const nextBillingRaw = profile?.next_billing_at ?? profile?.trial_end ?? null;
  const nextBillingLabel = nextBillingRaw ? format(new Date(nextBillingRaw), 'dd MMM yyyy') : 'N/A';

  return (
    <>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Impostazioni</h1>
        <p className="text-slate-400 text-sm mt-0.5">Profilo, sicurezza, privacy e gestione del piano</p>
      </div>

      <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
        {tabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={[
              'inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm whitespace-nowrap transition-all border',
              activeTab === tab.key
                ? 'bg-brand-500/20 text-brand-200 border-brand-500/40'
                : 'bg-slate-900 text-slate-300 border-slate-800 hover:bg-slate-800',
            ].join(' ')}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      <div className="max-w-4xl space-y-6">
        {activeTab === 'profilo' && (
          <div className="card p-6">
            <div className="flex items-center gap-2 mb-5">
              <User size={18} className="text-brand-400" />
              <h2 className="text-base font-semibold text-white">Profilo</h2>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-violet-600 flex items-center justify-center text-xl font-bold text-white">
                {(profile?.full_name ?? profile?.email ?? 'U').charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-white">{profile?.full_name ?? 'Nessun nome impostato'}</p>
                <p className="text-sm text-slate-400">{profile?.email}</p>
                <Badge variant={isPremium ? 'premium' : 'default'} className="mt-1">
                  {isPremium ? 'Premium' : 'Piano Free'}
                </Badge>
              </div>
            </div>

            <form onSubmit={profileForm.handleSubmit(onSaveProfile)} className="space-y-4">
              <Input
                label="Nome completo"
                placeholder="Mario Rossi"
                error={profileForm.formState.errors.full_name?.message}
                {...profileForm.register('full_name')}
              />
              <Input label="Email" value={profile?.email ?? ''} disabled className="opacity-60 cursor-not-allowed" />
              <div className="flex flex-wrap gap-2">
                <Button type="submit" loading={profileForm.formState.isSubmitting}>
                  Salva profilo
                </Button>
                <Button type="button" variant="danger" onClick={handleSignOut} leftIcon={<LogOut size={15} />}>
                  Logout
                </Button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="card p-6">
            <div className="flex items-center gap-2 mb-5">
              <CreditCard size={18} className="text-emerald-400" />
              <h2 className="text-base font-semibold text-white">Piano & Billing</h2>
            </div>

            {isPremium ? (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-brand-900/40 to-violet-900/40 border border-brand-500/25">
                <Sparkles size={18} className="text-brand-400 mt-0.5" />
                <div className="w-full">
                  <p className="font-semibold text-brand-300">Piano Premium attivo</p>
                  <p className="text-sm text-slate-400 mt-1">Hai accesso completo a tutte le funzionalita avanzate.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2">
                      <p className="text-[11px] text-slate-400">Piano</p>
                      <p className="text-sm text-slate-100 font-medium">{planLabel(profile?.plan)}</p>
                    </div>
                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2">
                      <p className="text-[11px] text-slate-400">Prossimo addebito</p>
                      <p className="text-sm text-slate-100 font-medium">{nextBillingLabel}</p>
                    </div>
                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2">
                      <p className="text-[11px] text-slate-400">Referral attivi</p>
                      <p className="text-sm text-slate-100 font-medium">{profile?.referrals_count ?? 0}/10</p>
                    </div>
                  </div>

                  <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/30 p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Share2 size={16} className="text-amber-400" />
                      <h3 className="text-sm font-semibold text-white">Referral & Inviti</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs text-slate-400 mb-1">Il tuo codice invito</p>
                        <p className="text-[11px] text-slate-500 mb-2">Massimo 10 persone invitate per account.</p>
                        <div className="flex items-center gap-2">
                          <Input value={inviteCode || 'Generazione...'} disabled className="font-mono tracking-wider" />
                          <Button variant="secondary" onClick={copyInviteLink} disabled={!inviteLink}>Copia link</Button>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 mb-1">Hai ricevuto un invito?</p>
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Inserisci codice invito"
                            value={inviteCodeInput}
                            onChange={(e) => setInviteCodeInput(e.target.value)}
                            className="uppercase"
                          />
                          <Button variant="secondary" onClick={joinWithInvite} loading={joiningInvite}>Unisciti</Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 border-t border-slate-700 pt-4">
                    <div className="mb-3 rounded-lg border border-sky-900/60 bg-sky-950/20 p-3">
                      <p className="text-xs font-medium text-sky-300">Come cambiare piano o disdire</p>
                      <p className="mt-1 text-xs text-slate-300">
                        Dopo il click si apre il portale Stripe. Vai su <span className="font-semibold">Subscription</span> e poi:
                      </p>
                      <p className="mt-1 text-xs text-slate-300">1) <span className="font-semibold">Update plan</span> per cambiare piano</p>
                      <p className="text-xs text-slate-300">2) <span className="font-semibold">Cancel plan</span> per annullare rinnovo</p>
                    </div>
                    <Button variant="danger" onClick={handleManageSubscription}>
                      Disdici / Gestisci abbonamento
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-400">Passa a Premium Monthly (5 euro/mese) o Annual (40 euro/anno) per sbloccare:</p>
                <ul className="space-y-2">
                  {premiumFeatures.map(f => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                      <Check size={14} className="text-emerald-400 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button onClick={handleUpgrade} loading={upgradingPlan} leftIcon={<Sparkles size={15} />}>
                  Vai ai piani Premium
                </Button>

                <div className="rounded-xl border border-slate-800 bg-slate-900/30 p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Share2 size={16} className="text-amber-400" />
                    <h3 className="text-sm font-semibold text-white">Referral & Inviti</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Il tuo codice invito</p>
                      <p className="text-[11px] text-slate-500 mb-2">Massimo 10 persone invitate per account.</p>
                      <div className="flex items-center gap-2">
                        <Input value={inviteCode || 'Generazione...'} disabled className="font-mono tracking-wider" />
                        <Button variant="secondary" onClick={copyInviteLink} disabled={!inviteLink}>Copia link</Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Hai ricevuto un invito?</p>
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Inserisci codice invito"
                          value={inviteCodeInput}
                          onChange={(e) => setInviteCodeInput(e.target.value)}
                          className="uppercase"
                        />
                        <Button variant="secondary" onClick={joinWithInvite} loading={joiningInvite}>Unisciti</Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'notifiche' && (
          <div className="card p-6 space-y-6">
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-amber-400" />
              <h2 className="text-base font-semibold text-white">Notifiche</h2>
            </div>

            <div className="space-y-4">
              {[
                { key: 'Over-budget', e: 'emailOverBudget', p: 'pushOverBudget' },
                { key: 'Spese ricorrenti', e: 'emailRecurring', p: 'pushRecurring' },
                { key: 'Consigli intelligenti', e: 'emailTips', p: 'pushTips' },
                { key: 'Report settimanale', e: 'emailWeeklyReport', p: 'pushTips' },
              ].map(item => (
                <div key={item.key} className="rounded-xl border border-slate-800 p-4 bg-slate-900/40">
                  <p className="text-sm font-medium text-slate-100 mb-3">{item.key}</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="flex items-center justify-between rounded-lg border border-slate-700 px-3 py-2">
                      <span className="text-sm text-slate-300">Email</span>
                      <input
                        type="checkbox"
                        checked={notificationPrefs[item.e as keyof NotificationPrefs] as boolean}
                        onChange={e =>
                          setNotificationPrefs(prev => ({
                            ...prev,
                            [item.e]: e.target.checked,
                          }))
                        }
                        className="accent-brand-500"
                      />
                    </label>
                    {item.key !== 'Report settimanale' ? (
                      <label className="flex items-center justify-between rounded-lg border border-slate-700 px-3 py-2">
                        <span className="text-sm text-slate-300">Push</span>
                        <input
                          type="checkbox"
                          checked={notificationPrefs[item.p as keyof NotificationPrefs] as boolean}
                          onChange={(e) => {
                            void togglePushPref(item.p as 'pushOverBudget' | 'pushRecurring' | 'pushTips', e.target.checked);
                          }}
                          className="accent-brand-500"
                        />
                      </label>
                    ) : (
                      <div className="rounded-lg border border-slate-700 px-3 py-2 text-xs text-slate-400">
                        Inviato via email ogni lunedi (Premium)
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-slate-800 p-4 bg-slate-900/40">
              <p className="text-sm font-medium text-slate-100 mb-1">Budget mensile per categoria (Premium)</p>
              <p className="text-xs text-slate-500 mb-3">Alert automatico quando superi la soglia impostata.</p>

              {!isPremium ? (
                <p className="text-xs text-amber-300">Upgrade a Premium per attivare budget per categoria.</p>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {budgetCategories.map((category) => (
                      <div key={category} className="rounded-lg border border-slate-700 px-3 py-2">
                        <label className="text-xs text-slate-300 capitalize">{getCategoryLabel(category)}</label>
                        <input
                          type="number"
                          value={categoryBudgets[category] ?? ''}
                          onChange={(e) => setCategoryBudgets((prev) => ({ ...prev, [category]: e.target.value }))}
                          className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-sm text-slate-200"
                          placeholder="es. 400"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <Button variant="secondary" onClick={saveCategoryBudgets} loading={savingBudgets}>
                      Salva budget categorie
                    </Button>
                  </div>
                </>
              )}
            </div>

            <div className="rounded-xl border border-slate-800 p-4 bg-slate-900/40">
              <p className="text-sm font-medium text-slate-100 mb-3">Anticipo notifica spese ricorrenti</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: '1 settimana', value: 7 },
                  { label: '5 giorni', value: 5 },
                  { label: '3 giorni', value: 3 },
                  { label: '1 giorno', value: 1 },
                ].map((opt) => {
                  const checked = notificationPrefs.recurringReminderOffsets.includes(opt.value);
                  return (
                    <label key={opt.value} className="flex items-center justify-between rounded-lg border border-slate-700 px-3 py-2">
                      <span className="text-xs text-slate-300">{opt.label}</span>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setNotificationPrefs(prev => {
                            const next = e.target.checked
                              ? Array.from(new Set([...prev.recurringReminderOffsets, opt.value]))
                              : prev.recurringReminderOffsets.filter(v => v !== opt.value);
                            return {
                              ...prev,
                              recurringReminderOffsets: next.sort((a, b) => b - a),
                            };
                          });
                        }}
                        className="accent-brand-500"
                      />
                    </label>
                  );
                })}
              </div>
            </div>

            <Button onClick={saveNotifications} loading={savingNotifications}>Salva preferenze notifiche</Button>
          </div>
        )}

        {activeTab === 'categorie' && (
          <div className="card p-6 space-y-6">
            <div className="flex items-center gap-2">
              <Tags size={18} className="text-violet-400" />
              <h2 className="text-base font-semibold text-white">Categorie personalizzate</h2>
            </div>

            <div className="grid sm:grid-cols-[120px_1fr_auto] gap-3">
              <Input
                label="Emoji"
                value={newCategoryEmoji}
                onChange={e => setNewCategoryEmoji(e.target.value)}
                maxLength={4}
              />
              <Input
                label="Nome categoria"
                placeholder="Es. Viaggi"
                value={newCategoryName}
                onChange={e => setNewCategoryName(e.target.value)}
              />
              <div className="self-end">
                <Button onClick={addCustomCategory} loading={savingCategory}>Aggiungi</Button>
              </div>
            </div>

            {!isPremium && (
              <p className="text-xs text-slate-500">Piano Free: fino a {maxCustomCategories} categorie custom.</p>
            )}

            <div className="space-y-2">
              {categories.length === 0 ? (
                <p className="text-sm text-slate-400">Nessuna categoria personalizzata trovata.</p>
              ) : (
                categories.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between rounded-xl border border-slate-800 px-4 py-3 bg-slate-900/40">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{cat.emoji || '🏷️'}</span>
                      <div>
                        <p className="text-sm font-medium text-slate-100">{cat.name.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}</p>
                        <p className="text-xs text-slate-500">Utilizzi: {cat.count ?? 0}</p>
                      </div>
                    </div>
                    <Button variant="ghost" onClick={() => deleteCustomCategory(cat.id)}>Elimina</Button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'sicurezza' && (
          <div className="space-y-6">
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-5">
                <Shield size={18} className="text-blue-400" />
                <h2 className="text-base font-semibold text-white">Password</h2>
              </div>

              <form onSubmit={pwForm.handleSubmit(onChangePassword)} className="space-y-4">
                <Input
                  type="password"
                  label="Nuova password"
                  placeholder="Min. 8 caratteri, 1 maiuscola, 1 numero"
                  autoComplete="new-password"
                  error={pwForm.formState.errors.password?.message}
                  {...pwForm.register('password')}
                />
                <Input
                  type="password"
                  label="Conferma nuova password"
                  placeholder="Ripeti password"
                  autoComplete="new-password"
                  error={pwForm.formState.errors.confirmPassword?.message}
                  {...pwForm.register('confirmPassword')}
                />
                <Button type="submit" variant="secondary" loading={pwForm.formState.isSubmitting}>
                  Aggiorna password
                </Button>
              </form>
            </div>

            <div className="card p-6 space-y-4">
              <div className="flex items-center gap-2">
                <QrCode size={18} className="text-emerald-400" />
                <h2 className="text-base font-semibold text-white">MFA TOTP</h2>
                <Badge variant={hasEnrolledMfa ? 'premium' : 'default'}>{hasEnrolledMfa ? 'Attivo' : 'Non attivo'}</Badge>
              </div>

              <Input
                label="Nome dispositivo autenticatore"
                value={mfaFriendlyName}
                onChange={e => setMfaFriendlyName(e.target.value)}
                placeholder="BudgetAI Authenticator"
              />

              <div className="flex flex-wrap gap-2">
                <Button onClick={enrollTotp} loading={enrollingMfa} leftIcon={<Smartphone size={15} />}>
                  Enroll QR
                </Button>
                <Button variant="secondary" onClick={refreshMfaFactors} loading={mfaLoading}>
                  Aggiorna stato
                </Button>
                <Button variant="ghost" onClick={disableMfa} disabled={!hasEnrolledMfa || mfaLoading}>
                  Disattiva MFA
                </Button>
              </div>

              {totpQrCode && (
                <div className="rounded-xl border border-slate-800 p-4 bg-slate-900/40 space-y-3">
                  <p className="text-sm text-slate-300">1) Scannerizza il QR con Google Authenticator / 1Password / Authy.</p>
                  <img src={totpQrCode} alt="QR code MFA" className="w-52 h-52 bg-white p-2 rounded-lg" />
                  <p className="text-xs text-slate-500 break-all">URI di fallback: {totpUri}</p>
                  <div className="flex flex-wrap gap-2 items-end">
                    <Input
                      label="Codice a 6 cifre"
                      value={totpCode}
                      onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                    />
                    <Button onClick={verifyTotp} loading={verifyingMfa}>Verifica codice</Button>
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-slate-800 p-4 bg-slate-900/40">
                <p className="text-sm text-slate-200 mb-2">Backup codes</p>
                <p className="text-xs text-slate-500 mb-3">Genera e scarica codici di recupero monouso per accesso d'emergenza.</p>
                <Button variant="secondary" onClick={downloadBackupCodes} leftIcon={<Download size={14} />}>
                  Scarica backup codes
                </Button>
              </div>
            </div>

            <div className="card p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Shield size={18} className="text-cyan-400" />
                <h2 className="text-base font-semibold text-white">Sessioni attive</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-800 px-3 py-2 bg-slate-900/40">
                  <p className="text-[11px] text-slate-400">Sessione corrente</p>
                  <p className="text-sm text-slate-100">{loadingSessions ? 'Caricamento...' : currentSessionLabel}</p>
                </div>
                <div className="rounded-lg border border-slate-800 px-3 py-2 bg-slate-900/40">
                  <p className="text-[11px] text-slate-400">Livello assicurazione (AAL)</p>
                  <p className="text-sm text-slate-100">{loadingSessions ? 'Caricamento...' : aalLabel}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={refreshSessionInfo} loading={loadingSessions}>Aggiorna sessioni</Button>
                <Button variant="danger" onClick={handleSignOutAll} loading={loggingOutAll}>Logout all</Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'privacy' && (
          <div className="space-y-6">
            <div className="card p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Lock size={18} className="text-emerald-400" />
                <h2 className="text-base font-semibold text-white">Privacy/GDPR</h2>
              </div>
              <p className="text-sm text-slate-400">Esercita i tuoi diritti GDPR: portabilita (download JSON) e cancellazione completa dell'account.</p>
              <Button onClick={downloadUserData} loading={downloadingData} leftIcon={<Download size={14} />}>
                Download dati JSON
              </Button>
            </div>

            <div className="card p-6 border-red-500/20">
              <h2 className="text-base font-semibold text-red-400 mb-2">Eliminazione account (cascade)</h2>
              <p className="text-sm text-slate-400 mb-4">Questa azione elimina in modo permanente account, profilo, spese, categorie, entrate e referral associati.</p>
              <Button variant="danger" onClick={() => setOpenDeleteModal(true)} leftIcon={<Trash2 size={14} />}>
                Elimina account
              </Button>
            </div>
          </div>
        )}

      </div>

      <Modal open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} title="Conferma eliminazione account" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-slate-300">Per confermare la cancellazione definitiva, scrivi <strong>ELIMINA</strong>.</p>
          <Input
            value={deleteConfirmText}
            onChange={e => setDeleteConfirmText(e.target.value)}
            placeholder="ELIMINA"
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpenDeleteModal(false)}>
              Annulla
            </Button>
            <Button variant="danger" onClick={deleteAccountCascade} loading={deletingAccount}>
              Elimina definitivamente
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
